import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getBiodataMatches, addToShortlist, sendInterest,
  getShortlist, getInterests,
  getUserActivePlans, getBoostedProfiles
} from '../../Api/CoreApi';
import {
  FaSearch, FaHeart, FaStar, FaArrowRight, FaUser, FaUsers,
  FaMapMarkerAlt, FaGraduationCap, FaBriefcase, FaEyeSlash, FaEye,
  FaSpinner, FaCheck, FaPhone, FaLock
} from 'react-icons/fa';
import { handleShortlistUser, fetchShortlistedUsers, addShortlistListener } from '../../utils/shortlistUtils';
import { message } from 'antd';

const BiodataMatches = () => {
  const navigate = useNavigate();
  const [topMatch, setTopMatch] = useState({});
  const [currentUser, setCurrentUser] = useState({});
  const [matchSummary, setMatchSummary] = useState([]);
  const [moreMatches, setMoreMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [shortlistedIds, setShortlistedIds] = useState([]);
  const [interestSentIds, setInterestSentIds] = useState([]);
  const [hiddenIds, setHiddenIds] = useState([]);
  const [actionLoading, setActionLoading] = useState({});
  const [filteredMatches, setFilteredMatches] = useState([]);
  const [activePlans, setActivePlans] = useState([]);
  const [viewedContacts, setViewedContacts] = useState([]);
  const [boostedIds, setBoostedIds] = useState([]);

  // ============================================
  // HELPER
  // ============================================
  // ============================================
  // HELPER (UPDATED)
  // ============================================
  const getDisplayId = (user) => {
    if (!user) return 'N/A';
    if (user.profile_id) return `${user.profile_id}`;
    if (user.User_id || user.user_id || user.id) return user.User_id || user.user_id || user.id;
    return 'N/A';
  };

  const canViewContact = (userId) => {
    if (viewedContacts.includes(userId)) return true;
    // ⭐ Check ALL contact view plans (multiple support)
    const contactPlans = activePlans.filter(p => p.plan_type === 'contact_view');
    const totalRemaining = contactPlans.reduce((sum, p) => {
      if (p.contact_view_limit >= 9999) return 999999;
      return sum + (p.remaining_views || 0);
    }, 0);
    return totalRemaining > 0;
  };

  // ⭐ Get TOTAL remaining views from ALL contact plans
  const getTotalRemainingViews = () => {
    const contactPlans = activePlans.filter(p => p.plan_type === 'contact_view');
    if (contactPlans.length === 0) return 0;

    const isUnlimited = contactPlans.some(p => p.contact_view_limit >= 9999);
    if (isUnlimited) return '∞';

    return contactPlans.reduce((sum, p) => sum + (p.remaining_views || 0), 0);
  };

  // ⭐ Get TOTAL contact plans
  const getContactPlansCount = () => {
    return activePlans.filter(p => p.plan_type === 'contact_view').length;
  };

  const hasBoostPlan = () => activePlans.some(p => p.plan_type === 'profile_boost');

  const getBoostPosition = () => {
    const plan = activePlans.find(p => p.plan_type === 'profile_boost');
    if (!plan) return '';
    if (plan.boost_position === 'top') return 'Top Position';
    if (plan.boost_position === 'featured') return 'Featured Section';
    return 'Highlighted';
  };

  // ⭐ Get boost plans count
  const getBoostPlansCount = () => {
    return activePlans.filter(p => p.plan_type === 'profile_boost').length;
  };

  // ⭐ Get plan type icon
  const getPlanIcon = (type) => {
    const icons = {
      contact_view: '📱',
      profile_boost: '🚀',
      specific_user: '👤',
      custom: '⚙️',
    };
    return icons[type] || '💎';
  };

  // ============================================
  // useEffect
  // ============================================
  useEffect(() => {
    fetchUserPlans();
    fetchBoostedProfilesData();
    fetchUserInteractions();

    const unsubscribe = addShortlistListener((newIds) => {
      setShortlistedIds(newIds);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (activePlans !== undefined) {
      fetchMatchData();
    }
  }, [boostedIds]);

  // ============================================
  // FETCH FUNCTIONS
  // ============================================
  const fetchUserPlans = async () => {
    try {
      const data = await getUserActivePlans();
      console.log('📦 Active Plans:', data);

      const plansArray = data?.plans || [];
      const viewedContactsArray = data?.viewed_contacts || [];

      setActivePlans(plansArray);
      setViewedContacts(viewedContactsArray);

      console.log(`✅ ${plansArray.length} active plans loaded`);
    } catch (err) {
      console.error("Plans error:", err);
      setActivePlans([]);
      setViewedContacts([]);
    }
  };

  const fetchBoostedProfilesData = async () => {
    try {
      const data = await getBoostedProfiles();
      setBoostedIds(data.boosted_user_ids || []);
    } catch (err) {
      console.error("Boost error:", err);
    }
  };

  const fetchMatchData = async () => {
    try {
      const data = await getBiodataMatches();

      const user = data.currentUser || {};
      const processedUser = {
        ...user,
        displayId: getDisplayId(user),
        userId: user.User_id || user.user_id || user.id,
      };
      setCurrentUser(processedUser);

      const rawTopMatch = data.topMatch || {};
      const userReligion = (processedUser.religion || '').toLowerCase().trim();
      const userCaste = (processedUser.caste || '').toLowerCase().trim();

      const topMatchReligion = (rawTopMatch.religion || '').toLowerCase().trim();
      const topMatchCaste = (rawTopMatch.caste || rawTopMatch.cast || '').toLowerCase().trim();

      const casteMatch = userCaste && topMatchCaste ? userCaste === topMatchCaste : false;
      const religionMatch = !topMatchReligion ? true : userReligion === topMatchReligion;

      if (casteMatch && religionMatch) {
        setTopMatch({
          ...rawTopMatch,
          displayId: getDisplayId(rawTopMatch),
          userId: rawTopMatch.User_id || rawTopMatch.user_id || rawTopMatch.id,
        });
      } else {
        setTopMatch({});
      }

      setMatchSummary(data.matchSummary || []);

      const matches = data.moreMatches || [];
      const processedMatches = matches.map(m => ({
        ...m,
        displayId: getDisplayId(m),
        userId: m.User_id || m.user_id || m.id,
        isBoosted: boostedIds.includes(m.User_id || m.user_id || m.id),
      }));

      processedMatches.sort((a, b) => {
        if (a.isBoosted && !b.isBoosted) return -1;
        if (!a.isBoosted && b.isBoosted) return 1;
        return 0;
      });

      setMoreMatches(processedMatches);

      const filtered = processedMatches.filter(m => {
        const mReligion = (m.religion || '').toLowerCase().trim();
        const mCaste = (m.caste || m.cast || '').toLowerCase().trim();
        const rMatch = !mReligion ? true : userReligion === mReligion;
        const cMatch = userCaste && mCaste ? userCaste === mCaste : false;
        return rMatch && cMatch;
      });

      setFilteredMatches(filtered);

    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserInteractions = async () => {
    try {
      const shortlisted = await fetchShortlistedUsers();
      setShortlistedIds(shortlisted || []);
      const interests = await getInterests();
      setInterestSentIds(interests.map(item => item.to_user));
    } catch (error) {
      console.error("Error:", error);
    }
  };

  // ============================================
  // HANDLERS
  // ============================================
  const handleSendInterest = async (userId) => {
    if (!userId) {
      message.error("Invalid user");
      return;
    }

    // ✅ CHECK - Profile unlock hai ya nahi
    const isUnlocked = viewedContacts.includes(userId);

    if (!isUnlocked) {
      // ✅ Lock hai - block karo aur message dikhao
      message.warning({
        content: (
          <div style={{ lineHeight: 1.6 }}>
            <strong>🔒 Profile Locked!</strong><br />
            <span style={{ fontSize: 12, color: '#888' }}>
              Please unlock this profile first to send interest.
            </span>
          </div>
        ),
        duration: 4,
      });
      return; // ← STOP KARO
    }

    // ✅ Unlocked hai - interest bhejo
    setActionLoading(prev => ({ ...prev, [userId]: 'interest' }));
    try {
      const response = await sendInterest(userId);
      if (response && response.id) {
        setInterestSentIds(prev => [...prev, userId]);
        message.success("💖 Interest sent successfully!");
      }
    } catch (error) {
      message.error("Failed to send interest");
    } finally {
      setActionLoading(prev => ({ ...prev, [userId]: null }));
    }
  };

  const handleShortlist = async (userId) => {
    setActionLoading(prev => ({ ...prev, [userId]: 'shortlist' }));
    await handleShortlistUser(userId, setShortlistedIds);
    setActionLoading(prev => ({ ...prev, [userId]: null }));
  };


  const handleViewProfile = (userId) => {
    if (userId) navigate(`/user-profile/${userId}`);
  };

  const handleViewAll = () => navigate('/all-users');

  const isMatchShortlisted = (id) => shortlistedIds.includes(id);
  const isInterestSent = (id) => interestSentIds.includes(id);
  const isMatchHidden = (id) => hiddenIds.includes(id);

  const radius = 56;
  const circumference = 2 * Math.PI * radius;
  const matchPct = topMatch.matchPercentage || 0;
  const dashOffset = circumference - (matchPct / 100) * circumference;

  // ============================================
  // LOADING
  // ============================================
  if (loading) {
    return (
      <>
        <style>{`@keyframes bmSpin { 0%{transform:rotate(0deg)} 100%{transform:rotate(360deg)} }`}</style>
        <div style={{
          display: 'flex', justifyContent: 'center', alignItems: 'center',
          minHeight: '60vh', flexDirection: 'column', gap: 16, background: '#fff',
          padding: '20px'
        }}>
          <FaSpinner size={40} style={{ color: '#ff0055', animation: 'bmSpin 1s linear infinite' }} />
          <p style={{ color: '#888', fontSize: 13, textAlign: 'center' }}>
            💖 Finding your perfect matches...
          </p>
        </div>
      </>
    );
  }

  // ============================================
  // CONTACT STATUS COMPONENT
  // ============================================
  const ContactStatus = ({ userId }) => {
    const isViewed = viewedContacts.includes(userId);
    const canView = canViewContact(userId);

    if (isViewed) {
      return (
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 4,
          fontSize: 10, fontWeight: 700, padding: '3px 8px',
          borderRadius: 8, margin: '5px 0',
          background: '#eff6ff', color: '#3b82f6',
          border: '1px solid #bfdbfe'
        }}>
          📱 Contact Unlocked
        </div>
      );
    }
    if (canView) {
      return (
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 4,
          fontSize: 10, fontWeight: 700, padding: '3px 8px',
          borderRadius: 8, margin: '5px 0',
          background: '#ecfdf5', color: '#10b981',
          border: '1px solid #a7f3d0'
        }}>
          ✅ Can View Contact
        </div>
      );
    }
    return (
      <div style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        fontSize: 10, fontWeight: 700, padding: '3px 8px',
        borderRadius: 8, margin: '5px 0',
        background: '#f5f5f5', color: '#999',
        border: '1px solid #e5e5e5'
      }}>
        🔒 Contact Locked
      </div>
    );
  };

  // ============================================
  // RENDER
  // ============================================
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800;900&display=swap');
        * { font-family: 'Poppins', sans-serif !important; box-sizing: border-box; margin: 0; }
        @keyframes bmSpin { 0%{transform:rotate(0deg)} 100%{transform:rotate(360deg)} }
        @keyframes bmPulse { 0%,100%{transform:scale(1)} 50%{transform:scale(1.05)} }
        @keyframes bmAvatarGlow { 0%,100%{background-position:0% 50%} 50%{background-position:100% 50%} }

        /* ===== PAGE ===== */
        .bm-page {
          background: #fff;
          min-height: 100vh;
          padding: 16px 12px;
        }
        .bm-wrap {
          max-width: 1280px;
          margin: 0 auto;
        }

        /* ===== HEADER ===== */
        .bm-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 14px;
          flex-wrap: wrap;
          gap: 10px;
        }
        .bm-title {
          font-size: 22px;
          font-weight: 800;
          color: #1a0a10;
          margin: 0;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .bm-title::before { content: '💕'; font-size: 20px; }

        /* ===== PLAN BANNER ===== */
        .bm-plan-banner {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 11px 14px;
          border-radius: 14px;
          margin-bottom: 10px;
          flex-wrap: wrap;
        }
        .bm-plan-text { flex: 1; min-width: 120px; }

        /* ===== MAIN GRID ===== */
        .bm-main-grid {
          display: grid;
          grid-template-columns: 2fr 1fr;
          gap: 16px;
          margin-bottom: 16px;
        }

        /* ===== CARD ===== */
        .bm-card {
          background: #fff;
          border-radius: 18px;
          border: 2px solid #ffe4ec;
          box-shadow: 0 6px 22px rgba(255,0,85,.05);
          padding: 20px;
          transition: all .3s ease;
          position: relative;
          overflow: hidden;
        }
        .bm-card:hover {
          border-color: #ffb3c8;
          box-shadow: 0 14px 36px rgba(255,0,85,.1);
        }
        .bm-card::before {
          content: "";
          position: absolute;
          top: 0; left: 0; right: 0;
          height: 4px;
          background: linear-gradient(90deg, #ff0055, #ff4d88, #ff80aa);
        }

        .bm-card-title {
          font-size: 16px;
          font-weight: 800;
          color: #1a0a10;
          margin: 0 0 16px;
          display: flex;
          align-items: center;
          gap: 7px;
          padding-bottom: 12px;
          border-bottom: 2px solid #fff0f3;
        }
        .bm-card-title::before { content: '✨'; font-size: 14px; }

        /* ===== TOP MATCH GRID ===== */
        .bm-top-match {
          display: grid;
          grid-template-columns: 1fr auto 1fr;
          gap: 16px;
          align-items: center;
        }

        /* ===== PROFILE COL ===== */
        .bm-profile-col {
          text-align: center;
          padding: 16px 12px;
          background: linear-gradient(135deg, #fffafc, #fff0f3);
          border: 2px solid #ffe4ec;
          border-radius: 16px;
          transition: all .3s ease;
        }
        .bm-profile-col:hover {
          border-color: #ffb3c8;
          box-shadow: 0 8px 22px rgba(255,0,85,.08);
          transform: translateY(-3px);
        }

        .bm-profile-img-wrap {
          position: relative;
          width: 90px;
          height: 90px;
          margin: 0 auto 14px;
          padding: 4px;
          background: linear-gradient(135deg, #ff0055, #ff4d88, #ff80aa, #ff0055);
          background-size: 200% 200%;
          animation: bmAvatarGlow 3s ease infinite;
          border-radius: 50%;
          box-shadow: 0 8px 22px rgba(255,0,85,.2);
        }
        .bm-profile-img {
          width: 100%;
          height: 100%;
          border-radius: 50%;
          border: 3px solid #fff;
          object-fit: cover;
          display: block;
        }
        .bm-online-dot {
          position: absolute;
          bottom: 5px; right: 6px;
          width: 13px; height: 13px;
          border-radius: 50%;
          background: #4caf50;
          border: 2.5px solid #fff;
        }
        .bm-profile-name {
          font-size: 15px;
          font-weight: 800;
          color: #1a0a10;
          margin: 0 0 5px;
        }
        .bm-profile-name .you-tag {
          color: #ff0055;
          font-size: 10px;
          font-weight: 700;
          padding: 2px 7px;
          background: linear-gradient(135deg, #fff0f3, #ffe4ec);
          border-radius: 999px;
          border: 1px solid #ffd3df;
          margin-left: 4px;
        }
        .bm-profile-bid {
          font-size: 10px;
          color: #ff0055;
          font-weight: 800;
          margin: 0 0 10px;
          padding: 3px 12px;
          border-radius: 999px;
          background: linear-gradient(135deg, #fff0f3, #ffe4ec);
          display: inline-block;
          border: 1.5px solid #ffd3df;
        }
        .bm-profile-info {
          font-size: 11px;
          color: #666;
          line-height: 1.8;
          margin: 0 0 10px;
          text-align: left;
          padding: 8px 10px;
          background: #fff;
          border-radius: 10px;
          border: 1.5px solid #ffe4ec;
        }
        .bm-view-btn {
          padding: 8px 18px;
          border-radius: 999px;
          border: none;
          background: linear-gradient(135deg, #ff0055, #ff4d88);
          color: #fff;
          font-size: 11px;
          font-weight: 700;
          cursor: pointer;
          transition: all .3s ease;
          box-shadow: 0 4px 14px rgba(255,0,85,.2);
        }
        .bm-view-btn:hover { transform: translateY(-2px); }

        /* ===== CIRCLE ===== */
        .bm-circle-wrap {
          text-align: center;
          padding: 8px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
        }
        .bm-circle-label {
          font-size: 10px;
          color: #c2185b;
          font-weight: 800;
          margin-bottom: 8px;
          text-transform: uppercase;
          letter-spacing: 1px;
          padding: 3px 12px;
          background: linear-gradient(135deg, #fff0f3, #ffe4ec);
          border-radius: 999px;
          border: 1px solid #ffd3df;
        }
        .bm-circle-svg { transform: rotate(-90deg); }
        .bm-circle-bg { fill: none; stroke: #fff0f3; stroke-width: 12; }
        .bm-circle-progress {
          fill: none;
          stroke: url(#bmGrad);
          stroke-width: 12;
          stroke-linecap: round;
          transition: stroke-dashoffset 0.8s ease;
        }
        .bm-circle-text {
          position: relative;
          margin-top: -88px;
          font-size: 28px;
          font-weight: 900;
          background: linear-gradient(135deg, #ff0055, #ff4d88);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }
        .bm-circle-stars {
          color: #ffb300;
          font-size: 13px;
          margin-top: 10px;
          letter-spacing: 2px;
        }

        /* ===== ACTION ROW ===== */
        .bm-action-row {
          display: flex;
          gap: 8px;
          margin-top: 18px;
          justify-content: center;
          flex-wrap: wrap;
          padding-top: 18px;
          border-top: 2px solid #fff0f3;
        }
        .bm-action-btn {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 9px 16px;
          border-radius: 999px;
          border: none;
          font-size: 12px;
          font-weight: 700;
          cursor: pointer;
          transition: all .3s ease;
        }
        .bm-action-btn:disabled { opacity: 0.6; cursor: not-allowed; }
        .bm-btn-interest {
          background: linear-gradient(135deg, #ff0055, #ff4d88);
          color: #fff;
          box-shadow: 0 4px 14px rgba(255,0,85,.22);
        }
        .bm-btn-interest:hover:not(:disabled) { transform: translateY(-2px); }
        .bm-btn-shortlist {
          background: #fff;
          color: #ff0055;
          border: 1.5px solid #ff0055 !important;
        }
        .bm-btn-shortlisted {
          background: linear-gradient(135deg, #4caf50, #66bb6a) !important;
          color: #fff !important;
          border: none !important;
        }
        .bm-btn-hide {
          background: linear-gradient(135deg, #fff0f3, #ffe4ec);
          color: #c2185b;
          border: 1.5px solid #ffd3df !important;
        }
        .bm-btn-hidden {
          background: #9e9e9e !important;
          color: #fff !important;
        }

        /* ===== SUMMARY ===== */
        .bm-summary-list { display: flex; flex-direction: column; gap: 12px; }
        .bm-summary-row { display: flex; align-items: center; gap: 8px; }
        .bm-summary-icon {
          width: 28px; height: 28px;
          border-radius: 8px;
          display: flex; align-items: center; justify-content: center;
          font-size: 11px; color: #fff;
          flex-shrink: 0;
        }
        .bm-summary-label {
          font-size: 11.5px;
          color: #555;
          font-weight: 600;
          min-width: 90px;
          flex: 1;
        }
        .bm-summary-bar {
          flex: 1.5;
          height: 6px;
          background: #fff0f3;
          border-radius: 999px;
          overflow: hidden;
        }
        .bm-summary-fill {
          height: 100%;
          border-radius: 999px;
          transition: width 1s ease;
        }
        .bm-summary-pct {
          font-size: 11.5px;
          color: #1a0a10;
          font-weight: 800;
          min-width: 34px;
          text-align: right;
        }
        .bm-overall-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: 16px;
          padding-top: 14px;
          border-top: 2px solid #fff0f3;
        }
        .bm-overall-label { font-size: 12px; color: #555; font-weight: 700; }
        .bm-overall-value {
          font-size: 22px;
          font-weight: 900;
          background: linear-gradient(135deg, #4caf50, #66bb6a);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }

        /* ===== MORE MATCHES GRID ===== */
        .bm-more-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 14px;
          align-items: stretch;
        }

        /* ===== MORE CARD ===== */
        .bm-more-card {
          background: #fff;
          border: 2px solid #ffe4ec;
          border-radius: 18px;
          padding: 18px 14px;
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          transition: all .35s ease;
          position: relative;
          overflow: hidden;
        }
        .bm-more-card::before {
          content: "";
          position: absolute;
          top: 0; left: 0; right: 0;
          height: 50px;
          background: linear-gradient(135deg, #fff0f3, #ffe4ec);
        }
        .bm-more-card:hover {
          transform: translateY(-5px);
          border-color: #ff80aa;
          box-shadow: 0 12px 28px rgba(255,0,85,.1);
        }
        .bm-more-card.boosted {
          border-color: #ffd700 !important;
          box-shadow: 0 4px 16px rgba(255,215,0,.2);
        }

        .bm-boost-tag {
          position: absolute;
          top: 7px; right: 7px;
          padding: 2px 8px;
          background: linear-gradient(135deg, #ffd700, #ffb300);
          color: #1a0a10;
          border-radius: 999px;
          font-size: 8px;
          font-weight: 800;
          z-index: 5;
          animation: bmPulse 2s ease infinite;
        }

        .bm-more-img-wrap {
          position: relative;
          z-index: 2;
          padding: 3px;
          background: linear-gradient(135deg, #ff0055, #ff4d88, #ff80aa, #ff0055);
          background-size: 200% 200%;
          animation: bmAvatarGlow 3s ease infinite;
          border-radius: 50%;
          box-shadow: 0 6px 18px rgba(255,0,85,.2);
          margin-bottom: 12px;
          margin-top: 4px;
        }
        .bm-more-img {
          width: 66px; height: 66px;
          border-radius: 50%;
          border: 3px solid #fff;
          object-fit: cover;
          display: block;
        }
        .bm-more-info {
          flex: 1;
          width: 100%;
          position: relative;
          z-index: 2;
          display: flex;
          flex-direction: column;
          align-items: center;
        }
        .bm-more-name {
          font-size: 14px;
          font-weight: 800;
          color: #1a0a10;
          margin: 0 0 5px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          flex-wrap: wrap;
        }
        .bm-more-pct-tag {
          background: linear-gradient(135deg, #4caf50, #66bb6a);
          color: #fff;
          font-size: 9.5px;
          padding: 2px 8px;
          border-radius: 999px;
          font-weight: 700;
        }
        .bm-more-id {
          font-size: 10px;
          color: #ff0055;
          font-weight: 800;
          padding: 2px 10px;
          border-radius: 999px;
          background: linear-gradient(135deg, #fff0f3, #ffe4ec);
          border: 1px solid #ffd3df;
          margin-bottom: 8px;
        }
        .bm-more-details {
          display: flex;
          flex-direction: column;
          gap: 3px;
          width: 100%;
          margin-bottom: 8px;
        }
        .bm-more-detail {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 5px;
          font-size: 10.5px;
          color: #777;
          font-weight: 500;
          padding: 4px 8px;
          background: linear-gradient(135deg, #fffafc, #fff0f3);
          border: 1px solid #ffe4ec;
          border-radius: 7px;
        }
        .bm-more-detail svg { color: #ff4d88; font-size: 9px; flex-shrink: 0; }
        .bm-more-actions {
          display: flex;
          gap: 5px;
          width: 100%;
          margin-top: auto;
        }
        .bm-more-btn {
          flex: 1;
          height: 34px;
          border: none;
          border-radius: 9px;
          font-size: 12px;
          cursor: pointer;
          transition: all .25s ease;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .bm-more-btn:disabled { opacity: 0.5; cursor: not-allowed; }
        .bm-more-btn:hover:not(:disabled) { transform: translateY(-2px); }
        .bm-more-btn-interest {
          background: linear-gradient(135deg, #ff0055, #ff4d88);
          color: #fff;
        }
        .bm-more-btn-shortlist {
          background: #fff;
          color: #ff0055;
          border: 1.5px solid #ff0055 !important;
        }
        .bm-more-btn-shortlisted {
          background: linear-gradient(135deg, #4caf50, #66bb6a) !important;
          color: #fff !important;
          border: none !important;
        }
        .bm-more-btn-view {
          background: linear-gradient(135deg, #2196f3, #42a5f5);
          color: #fff;
        }

        /* ===== VIEW ALL BTN ===== */
        .bm-view-all-btn {
          padding: 14px;
          border-radius: 18px;
          border: 2px dashed #ff0055;
          background: linear-gradient(135deg, #fff0f3, #ffe4ec);
          color: #ff0055;
          cursor: pointer;
          transition: all .3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-direction: column;
          gap: 6px;
          font-size: 12px;
          font-weight: 800;
        }
        .bm-view-all-btn:hover {
          background: linear-gradient(135deg, #ff0055, #ff4d88);
          color: #fff;
          border-style: solid;
          transform: translateY(-4px);
        }

        /* =============================================
           TABLET - 769px to 1100px
           ============================================= */
        @media (max-width: 1100px) {
          .bm-main-grid {
            grid-template-columns: 1fr;
            gap: 14px;
          }
          .bm-more-grid {
            grid-template-columns: repeat(3, 1fr);
          }
          .bm-view-all-btn {
            flex-direction: row;
            gap: 8px;
          }
        }

        /* =============================================
           TABLET SMALL - 601px to 900px
           ============================================= */
        @media (max-width: 900px) {
          .bm-more-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        /* =============================================
           MOBILE - max 768px
           ============================================= */
        @media (max-width: 768px) {

          .bm-page { padding: 12px 10px; }

          .bm-title { font-size: 19px; }

          /* Top Match - stack vertically */
          .bm-top-match {
            grid-template-columns: 1fr;
            gap: 16px;
          }

          /* Circle in center */
          .bm-circle-wrap { order: -1; }
          .bm-circle-text { margin-top: -88px; }

          /* Action buttons - wrap */
          .bm-action-row { gap: 7px; }
          .bm-action-btn {
            flex: 1;
            min-width: 80px;
            padding: 9px 12px;
            font-size: 11.5px;
            justify-content: center;
          }

          /* Plan banner */
          .bm-plan-banner { padding: 10px 12px; }
          .bm-plan-text p:first-child { font-size: 12px; }
          .bm-plan-text p:last-child { font-size: 10px; }

          /* Summary label shorter */
          .bm-summary-label { min-width: 80px; font-size: 11px; }
        }

        /* =============================================
           MOBILE SMALL - max 600px
           ============================================= */
        @media (max-width: 600px) {

          .bm-more-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 10px;
          }

          /* More card compact */
          .bm-more-card { padding: 14px 10px; border-radius: 14px; }
          .bm-more-img { width: 58px; height: 58px; }
          .bm-more-name { font-size: 13px; }
          .bm-more-id { font-size: 9.5px; }
          .bm-more-detail { font-size: 10px; padding: 3px 6px; }

          /* Profile col compact */
          .bm-profile-img-wrap { width: 80px; height: 80px; }
          .bm-profile-name { font-size: 14px; }
          .bm-profile-info { font-size: 10.5px; }

          /* Card padding */
          .bm-card { padding: 16px; }

          /* Action btn full width */
          .bm-action-row { flex-direction: column; gap: 7px; }
          .bm-action-btn { width: 100%; justify-content: center; }

          .bm-view-all-btn {
            grid-column: 1 / -1;
            flex-direction: row;
            padding: 12px;
          }
        }

        /* =============================================
           MOBILE EXTRA SMALL - max 400px
           ============================================= */
        @media (max-width: 400px) {
          .bm-more-grid {
            grid-template-columns: 1fr;
          }

          .bm-more-card { padding: 16px 12px; }
          .bm-more-img { width: 68px; height: 68px; }

          .bm-circle-svg { width: 120px; height: 120px; }
          .bm-circle-text { font-size: 24px; margin-top: -76px; }

          .bm-title { font-size: 17px; }
          .bm-card { padding: 14px 12px; }

          .bm-view-all-btn {
            grid-column: 1 / -1;
          }

          /* Summary compact */
          .bm-summary-row { gap: 5px; }
          .bm-summary-icon { width: 24px; height: 24px; font-size: 10px; }
          .bm-summary-label { min-width: 70px; font-size: 10.5px; }
          .bm-summary-pct { min-width: 28px; font-size: 10.5px; }
        }
      `}</style>

      <div className="bm-page">
        <div className="bm-wrap">

          {/* ===== HEADER ===== */}
          <div className="bm-header">
            <h1 className="bm-title">Biodata Match</h1>
          </div>



          {/* ===== PLAN BANNERS - MULTIPLE PLANS SUPPORT ===== */}

          {/* Show total plans count if multiple */}
          {activePlans.length > 1 && (
            <div style={{
              background: 'linear-gradient(135deg, #fff0f3, #ffe4ec)',
              border: '2px solid #ffd3df',
              borderRadius: 14,
              padding: '10px 14px',
              marginBottom: 10,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 8,
            }}>
              <span style={{
                fontSize: 12,
                fontWeight: 800,
                color: '#c2185b',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}>
                💎 You have {activePlans.length} active plans
              </span>
              <button
                onClick={() => navigate('/recharge')}
                style={{
                  padding: '6px 14px',
                  background: 'linear-gradient(135deg, #ff0055, #ff4d88)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 999,
                  fontSize: 10.5,
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                Manage Plans
              </button>
            </div>
          )}

          {/* Contact View Plans - ONE combined banner */}
          {activePlans.filter(p => p.plan_type === 'contact_view').length > 0 && (
            <div className="bm-plan-banner" style={{
              background: 'linear-gradient(135deg, #eff6ff, #dbeafe)',
              border: '2px solid #bfdbfe',
            }}>
              <div style={{
                width: 38, height: 38, borderRadius: 10, flexShrink: 0,
                background: 'linear-gradient(135deg, #3b82f6, #60a5fa)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16
              }}>📱</div>
              <div className="bm-plan-text" style={{ flex: 1 }}>
                <p style={{ fontSize: 12, fontWeight: 800, color: '#1e40af', margin: 0 }}>
                  Contact View Plan Active
                  {getContactPlansCount() > 1 && (
                    <span style={{
                      marginLeft: 6, fontSize: 9, background: '#3b82f6',
                      color: '#fff', padding: '2px 6px', borderRadius: 999,
                      fontWeight: 700
                    }}>
                      {getContactPlansCount()} PLANS
                    </span>
                  )}
                </p>
                <p style={{ fontSize: 10, color: '#3b82f6', margin: '2px 0 0', fontWeight: 600 }}>
                  {getContactPlansCount() > 1
                    ? `Total views from ${getContactPlansCount()} plans combined`
                    : 'View contacts of matched profiles'
                  }
                </p>
              </div>
              <div style={{
                textAlign: 'center', padding: '4px 14px', flexShrink: 0,
                background: '#fff', borderRadius: 10, border: '1.5px solid #bfdbfe'
              }}>
                <div style={{ fontSize: 20, fontWeight: 900, color: '#3b82f6', lineHeight: 1 }}>
                  {getTotalRemainingViews()}
                </div>
                <div style={{ fontSize: 8, color: '#93c5fd', fontWeight: 700, marginTop: 2 }}>
                  VIEWS LEFT
                </div>
              </div>
            </div>
          )}

          {/* Profile Boost Plans */}
          {hasBoostPlan() && (
            <div className="bm-plan-banner" style={{
              background: 'linear-gradient(135deg, #fffbeb, #fef3c7)',
              border: '2px solid #fde68a',
            }}>
              <div style={{
                width: 38, height: 38, borderRadius: 10, flexShrink: 0,
                background: 'linear-gradient(135deg, #ffd700, #ffb300)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16
              }}>🚀</div>
              <div className="bm-plan-text" style={{ flex: 1 }}>
                <p style={{ fontSize: 12, fontWeight: 800, color: '#92400e', margin: 0 }}>
                  Profile Boost Active!
                  {getBoostPlansCount() > 1 && (
                    <span style={{
                      marginLeft: 6, fontSize: 9, background: '#ffb300',
                      color: '#fff', padding: '2px 6px', borderRadius: 999,
                      fontWeight: 700
                    }}>
                      {getBoostPlansCount()} BOOSTS
                    </span>
                  )}
                </p>
                <p style={{ fontSize: 10, color: '#b45309', margin: '2px 0 0', fontWeight: 500 }}>
                  Showing at <strong>{getBoostPosition()}</strong> on dashboards
                </p>
              </div>
              <span style={{
                padding: '4px 10px', flexShrink: 0,
                background: 'linear-gradient(135deg, #ffd700, #ffb300)',
                color: '#1a0a10', borderRadius: 999,
                fontSize: 9, fontWeight: 800, whiteSpace: 'nowrap',
                animation: 'bmPulse 2s ease infinite'
              }}>
                ⭐ BOOSTED
              </span>
            </div>
          )}

          {/* Specific User Plans */}
          {activePlans.filter(p => p.plan_type === 'specific_user').length > 0 && (() => {
            const specificPlans = activePlans.filter(p => p.plan_type === 'specific_user');
            return (
              <div className="bm-plan-banner" style={{
                background: 'linear-gradient(135deg, #f5f3ff, #ede9fe)',
                border: '2px solid #ddd6fe',
              }}>
                <div style={{
                  width: 38, height: 38, borderRadius: 10, flexShrink: 0,
                  background: 'linear-gradient(135deg, #8b5cf6, #a78bfa)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16
                }}>👤</div>
                <div className="bm-plan-text" style={{ flex: 1 }}>
                  <p style={{ fontSize: 12, fontWeight: 800, color: '#5b21b6', margin: 0 }}>
                    Special Plan Active
                    {specificPlans.length > 1 && (
                      <span style={{
                        marginLeft: 6, fontSize: 9, background: '#8b5cf6',
                        color: '#fff', padding: '2px 6px', borderRadius: 999,
                        fontWeight: 700
                      }}>
                        {specificPlans.length} PLANS
                      </span>
                    )}
                  </p>
                  <p style={{ fontSize: 10, color: '#8b5cf6', margin: '2px 0 0', fontWeight: 500 }}>
                    View specific user profiles
                  </p>
                </div>
                <span style={{
                  padding: '4px 10px', flexShrink: 0,
                  background: 'linear-gradient(135deg, #8b5cf6, #a78bfa)',
                  color: '#fff', borderRadius: 999,
                  fontSize: 9, fontWeight: 800, whiteSpace: 'nowrap'
                }}>
                  👤 SPECIAL
                </span>
              </div>
            );
          })()}

          {/* Custom Plans */}
          {activePlans.filter(p => p.plan_type === 'custom').length > 0 && (() => {
            const customPlans = activePlans.filter(p => p.plan_type === 'custom');
            return (
              <div className="bm-plan-banner" style={{
                background: 'linear-gradient(135deg, #ecfdf5, #d1fae5)',
                border: '2px solid #a7f3d0',
              }}>
                <div style={{
                  width: 38, height: 38, borderRadius: 10, flexShrink: 0,
                  background: 'linear-gradient(135deg, #10b981, #34d399)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16
                }}>⚙️</div>
                <div className="bm-plan-text" style={{ flex: 1 }}>
                  <p style={{ fontSize: 12, fontWeight: 800, color: '#065f46', margin: 0 }}>
                    Custom Plan Active
                    {customPlans.length > 1 && (
                      <span style={{
                        marginLeft: 6, fontSize: 9, background: '#10b981',
                        color: '#fff', padding: '2px 6px', borderRadius: 999,
                        fontWeight: 700
                      }}>
                        {customPlans.length} PLANS
                      </span>
                    )}
                  </p>
                  <p style={{ fontSize: 10, color: '#059669', margin: '2px 0 0', fontWeight: 500 }}>
                    Premium custom features enabled
                  </p>
                </div>
                <span style={{
                  padding: '4px 10px', flexShrink: 0,
                  background: 'linear-gradient(135deg, #10b981, #34d399)',
                  color: '#fff', borderRadius: 999,
                  fontSize: 9, fontWeight: 800, whiteSpace: 'nowrap'
                }}>
                  ⚙️ CUSTOM
                </span>
              </div>
            );
          })()}

          {/* No Active Plans */}
          {activePlans.length === 0 && (
            <div className="bm-plan-banner" style={{
              background: 'linear-gradient(135deg, #fff0f3, #ffe4ec)',
              border: '2px solid #ffd3df',
              justifyContent: 'space-between',
            }}>
              <div className="bm-plan-text">
                <p style={{ fontSize: 12, fontWeight: 700, color: '#c2185b', margin: 0 }}>
                  🔒 Unlock contacts & boost profile!
                </p>
                <p style={{ fontSize: 10, color: '#e91e63', margin: '3px 0 0', fontWeight: 500 }}>
                  Purchase a plan to get more visibility
                </p>
              </div>
              <button
                onClick={() => navigate('/recharge')}
                style={{
                  padding: '8px 14px', flexShrink: 0,
                  background: 'linear-gradient(135deg, #ff0055, #ff4d88)',
                  color: '#fff', border: 'none', borderRadius: 999,
                  fontSize: 11, fontWeight: 700, cursor: 'pointer',
                  whiteSpace: 'nowrap'
                }}
              >
                View Plans
              </button>
            </div>
          )}

          {/* ===== MAIN GRID: TOP MATCH + SUMMARY ===== */}
          <div className="bm-main-grid">

            {/* Top Match Card */}
            <div className="bm-card">
              <h3 className="bm-card-title">Top Match for You</h3>

              {topMatch && topMatch.userId ? (
                <>
                  <div className="bm-top-match">

                    {/* Match Profile */}
                    <div className="bm-profile-col">
                      <div className="bm-profile-img-wrap">
                        <img
                          src={topMatch?.image || `https://ui-avatars.com/api/?name=${topMatch?.name || 'Match'}&background=ff0055&color=fff&size=128`}
                          alt={topMatch?.name}
                          className="bm-profile-img"
                        />
                        {topMatch.online && <span className="bm-online-dot" />}
                      </div>
                      <h4 className="bm-profile-name">{topMatch?.name || 'Match'}</h4>
                      <div className="bm-profile-bid">ID: {topMatch.displayId || 'N/A'}</div>
                      <p className="bm-profile-info">
                        {topMatch.age || 0} Yrs, {topMatch.height || 'N/A'}<br />
                        📍 {topMatch.location || 'N/A'}<br />
                        🎓 {topMatch.education || 'N/A'}
                      </p>
                      <ContactStatus userId={topMatch.userId} />
                      <button
                        className="bm-view-btn"
                        onClick={() => handleViewProfile(topMatch.userId)}
                        style={{ marginTop: 8 }}
                      >
                        View Profile
                      </button>
                    </div>

                    {/* Match Circle */}
                    <div className="bm-circle-wrap">
                      <div className="bm-circle-label">Overall Match</div>
                      <svg width="130" height="130" className="bm-circle-svg">
                        <defs>
                          <linearGradient id="bmGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="#4caf50" />
                            <stop offset="100%" stopColor="#66bb6a" />
                          </linearGradient>
                        </defs>
                        <circle cx="65" cy="65" r={radius} className="bm-circle-bg" />
                        <circle
                          cx="65" cy="65" r={radius}
                          className="bm-circle-progress"
                          strokeDasharray={circumference}
                          strokeDashoffset={dashOffset}
                        />
                      </svg>
                      <div className="bm-circle-text">{matchPct}%</div>
                      <div className="bm-circle-stars">★ ★ ★ ★ ☆</div>
                    </div>

                    {/* Your Profile */}
                    <div className="bm-profile-col">
                      <div className="bm-profile-img-wrap">
                        <img
                          src={currentUser.image || `https://ui-avatars.com/api/?name=${currentUser.name || 'You'}&background=4CAF50&color=fff&size=128`}
                          alt={currentUser.name}
                          className="bm-profile-img"
                        />
                        {currentUser.online && <span className="bm-online-dot" />}
                      </div>
                      <h4 className="bm-profile-name">
                        {currentUser.name || 'You'}
                        <span className="you-tag">(You)</span>
                      </h4>
                      <div className="bm-profile-bid">ID: {currentUser.displayId || 'N/A'}</div>
                      <p className="bm-profile-info">
                        {currentUser.age || 0} Yrs, {currentUser.height || 'N/A'}<br />
                        📍 {currentUser.location || 'N/A'}<br />
                        🎓 {currentUser.education || 'N/A'}
                      </p>
                      {hasBoostPlan() && (
                        <div style={{
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                          fontSize: 9.5, fontWeight: 700, padding: '3px 8px',
                          borderRadius: 8, marginBottom: 7,
                          background: '#fffbeb', color: '#92400e',
                          border: '1px solid #fde68a'
                        }}>
                          🚀 Your Profile is Boosted
                        </div>
                      )}
                      <button
                        className="bm-view-btn"
                        onClick={() => handleViewProfile(currentUser.userId)}
                        style={{ marginTop: 8 }}
                      >
                        View Profile
                      </button>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="bm-action-row">
                    <button
                      className="bm-action-btn bm-btn-interest"
                      onClick={() => handleSendInterest(topMatch.userId)}
                      disabled={
                        actionLoading[topMatch.userId] === 'interest' ||
                        isInterestSent(topMatch.userId)
                      }
                      style={{
                        // ✅ Lock hai to gray/disabled look
                        ...(
                          !viewedContacts.includes(topMatch.userId) &&
                            !isInterestSent(topMatch.userId)
                            ? {
                              background: 'linear-gradient(135deg, #9e9e9e, #bdbdbd)',
                              boxShadow: 'none',
                              cursor: 'not-allowed',
                            }
                            : {}
                        )
                      }}
                      title={
                        !viewedContacts.includes(topMatch.userId)
                          ? "🔒 Unlock profile first to send interest"
                          : "Send Interest"
                      }
                    >
                      {actionLoading[topMatch.userId] === 'interest' ? (
                        <FaSpinner size={11} style={{ animation: 'bmSpin 1s linear infinite' }} />
                      ) : isInterestSent(topMatch.userId) ? (
                        <><FaCheck size={10} /> Sent</>
                      ) : !viewedContacts.includes(topMatch.userId) ? (
                        <><FaLock size={10} /> Unlock First</>  // ← Lock icon
                      ) : (
                        <><FaHeart size={11} /> Send Interest</>
                      )}
                    </button>

                    <button
                      className={`bm-action-btn bm-btn-shortlist ${isMatchShortlisted(topMatch.userId) ? 'bm-btn-shortlisted' : ''}`}
                      onClick={() => handleShortlist(topMatch.userId)}
                      disabled={actionLoading[topMatch.userId] === 'shortlist' || isMatchShortlisted(topMatch.userId)}
                    >
                      {actionLoading[topMatch.userId] === 'shortlist' ? (
                        <FaSpinner size={11} style={{ animation: 'bmSpin 1s linear infinite' }} />
                      ) : isMatchShortlisted(topMatch.userId) ? (
                        <>⭐ Shortlisted</>
                      ) : (
                        <><FaStar size={11} /> Shortlist</>
                      )}
                    </button>
                  </div>
                </>
              ) : (
                /* No Top Match */
                <div style={{ textAlign: 'center', padding: '30px 16px' }}>
                  <div className="bm-profile-col" style={{ maxWidth: 160, margin: '0 auto 20px' }}>
                    <div className="bm-profile-img-wrap">
                      <img
                        src={currentUser.image || `https://ui-avatars.com/api/?name=${currentUser.name || 'You'}&background=4CAF50&color=fff&size=128`}
                        alt="You"
                        className="bm-profile-img"
                      />
                    </div>
                    <h4 className="bm-profile-name">
                      {currentUser.name || 'You'}
                      <span className="you-tag">(You)</span>
                    </h4>
                    <div className="bm-profile-bid">ID: {currentUser.displayId || 'N/A'}</div>
                  </div>
                  <div style={{ fontSize: 40, marginBottom: 12 }}>💔</div>
                  <p style={{ fontSize: 15, fontWeight: 800, color: '#c2185b', marginBottom: 7 }}>
                    No Perfect Match Found Yet
                  </p>
                  <p style={{ fontSize: 12, color: '#aaa', marginBottom: 18, lineHeight: 1.7 }}>
                    No profile with same religion and caste is available right now. We'll notify you when a match is found! 💕
                  </p>
                  <button
                    onClick={handleViewAll}
                    style={{
                      padding: '10px 24px',
                      background: 'linear-gradient(135deg, #ff0055, #ff4d88)',
                      color: '#fff', border: 'none', borderRadius: 999,
                      fontSize: 12, fontWeight: 700, cursor: 'pointer'
                    }}
                  >
                    Browse All Profiles
                  </button>
                </div>
              )}
            </div>

            {/* Match Summary */}
            <div className="bm-card">
              <h3 className="bm-card-title">Match Summary</h3>
              <div className="bm-summary-list">
                {matchSummary.map((item, idx) => (
                  <div key={idx} className="bm-summary-row">
                    <div
                      className="bm-summary-icon"
                      style={{ background: `linear-gradient(135deg, ${item.color}, ${item.color}cc)` }}
                    >
                      <FaUser />
                    </div>
                    <span className="bm-summary-label">{item.label}</span>
                    <div className="bm-summary-bar">
                      <div
                        className="bm-summary-fill"
                        style={{
                          width: `${item.value}%`,
                          background: `linear-gradient(90deg, ${item.color}, ${item.color}cc)`
                        }}
                      />
                    </div>
                    <span className="bm-summary-pct">{item.value}%</span>
                  </div>
                ))}
              </div>
              <div className="bm-overall-row">
                <span className="bm-overall-label">💖 Overall Compatibility</span>
                <span className="bm-overall-value">{matchPct}%</span>
              </div>
            </div>
          </div>

          {/* ===== MORE MATCHES ===== */}
          <div className="bm-card">
            <h3 className="bm-card-title">More Matches for You</h3>

            {/* Info Banner */}
            <div style={{
              background: 'linear-gradient(135deg, #fff0f3, #ffe4ec)',
              border: '1.5px solid #ffd3df',
              borderRadius: 10, padding: '9px 14px', marginBottom: 14,
              display: 'flex', alignItems: 'center',
              justifyContent: 'space-between', flexWrap: 'wrap', gap: 7
            }}>
              <span style={{ fontSize: 11.5, color: '#c2185b', fontWeight: 700 }}>
                🎯 Same Religion + Caste Profiles
                {boostedIds.length > 0 && (
                  <span style={{ marginLeft: 6, color: '#f59e0b', fontSize: 10 }}>
                    🚀 Boosted first
                  </span>
                )}
              </span>
              <span style={{
                fontSize: 10, background: '#fff', padding: '3px 9px',
                borderRadius: 999, border: '1px solid #ffd3df', color: '#888'
              }}>
                {filteredMatches.length} profiles
              </span>
            </div>

            <div className="bm-more-grid">
              {filteredMatches.length > 0 ? (
                filteredMatches.map((m) => {
                  const matchId = m.userId;
                  const isShortlisted = isMatchShortlisted(matchId);
                  const isInterest = isInterestSent(matchId);
                  const isLoad = actionLoading[matchId];
                  const isHidden = isMatchHidden(matchId);
                  const isBoosted = boostedIds.includes(matchId) || m.isBoosted;

                  if (isHidden) return null;

                  return (
                    <div
                      key={matchId}
                      className={`bm-more-card ${isBoosted ? 'boosted' : ''}`}
                    >
                      {isBoosted && (
                        <div className="bm-boost-tag">🚀 BOOSTED</div>
                      )}

                      <div className="bm-more-img-wrap">
                        <img
                          src={m.image || `https://ui-avatars.com/api/?name=${m.name || 'Match'}&background=ff4d88&color=fff&size=128`}
                          alt={m.name}
                          className="bm-more-img"
                        />
                      </div>

                      <div className="bm-more-info">
                        <h4 className="bm-more-name">
                          {m.name || 'Match'}
                          <span className="bm-more-pct-tag">
                            {m.matchPercentage || 100}%
                          </span>
                        </h4>

                        <div className="bm-more-id">ID: {m.displayId || 'N/A'}</div>

                        <div style={{
                          fontSize: 10, color: '#ff0055', fontWeight: 700,
                          padding: '2px 8px',
                          background: 'linear-gradient(135deg, #fff0f3, #ffe4ec)',
                          border: '1px solid #ffd3df', borderRadius: 999,
                          marginBottom: 7, display: 'inline-block'
                        }}>
                          🕉️ {m.caste || m.cast || 'N/A'}
                        </div>

                        <div className="bm-more-details">
                          <div className="bm-more-detail">
                            <FaUser size={8} /> {m.age || 0} Yrs, {m.height || 'N/A'}
                          </div>
                          <div className="bm-more-detail">
                            <FaMapMarkerAlt size={8} /> {m.location || m.city || 'N/A'}
                          </div>
                          <div className="bm-more-detail">
                            <FaGraduationCap size={8} /> {m.education || m.course || 'N/A'}
                          </div>
                        </div>

                        <ContactStatus userId={matchId} />

                        <div className="bm-more-actions">
                          <button
                            className="bm-more-btn bm-more-btn-interest"
                            onClick={() => handleSendInterest(matchId)}
                            disabled={isLoad === 'interest' || isInterest}
                            style={{
                              // ✅ Lock hai to gray look
                              ...(
                                !viewedContacts.includes(matchId) && !isInterest
                                  ? {
                                    background: 'linear-gradient(135deg, #9e9e9e, #bdbdbd)',
                                    cursor: 'not-allowed',
                                  }
                                  : {}
                              )
                            }}
                            title={
                              !viewedContacts.includes(matchId)
                                ? "🔒 Unlock profile first"
                                : "Send Interest"
                            }
                          >
                            {isLoad === 'interest' ? (
                              <FaSpinner size={10} style={{ animation: 'bmSpin 1s linear infinite' }} />
                            ) : isInterest ? (
                              '✅'
                            ) : !viewedContacts.includes(matchId) ? (
                              <FaLock size={10} />   // ← Lock icon
                            ) : (
                              <FaHeart size={11} />
                            )}
                          </button>

                          <button
                            className={`bm-more-btn ${isShortlisted ? 'bm-more-btn-shortlisted' : 'bm-more-btn-shortlist'}`}
                            onClick={() => handleShortlist(matchId)}
                            disabled={isLoad === 'shortlist' || isShortlisted}
                            title="Shortlist"
                          >
                            {isLoad === 'shortlist' ? (
                              <FaSpinner size={10} style={{ animation: 'bmSpin 1s linear infinite' }} />
                            ) : isShortlisted ? '⭐' : <FaStar size={11} />}
                          </button>

                          <button
                            className="bm-more-btn bm-more-btn-view"
                            onClick={() => handleViewProfile(matchId)}
                            title="View Profile"
                          >
                            <FaEye size={11} />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div style={{
                  gridColumn: '1 / -1',
                  textAlign: 'center',
                  padding: '40px 16px'
                }}>
                  <div style={{ fontSize: 44, marginBottom: 12 }}>💔</div>
                  <p style={{ fontSize: 15, fontWeight: 800, color: '#c2185b', marginBottom: 7 }}>
                    No Perfect Match Found
                  </p>
                  <p style={{ fontSize: 11.5, color: '#aaa', marginBottom: 14, lineHeight: 1.6 }}>
                    No profile with same religion and caste available right now
                  </p>
                  <button
                    onClick={handleViewAll}
                    style={{
                      padding: '9px 22px',
                      background: 'linear-gradient(135deg, #ff0055, #ff4d88)',
                      color: '#fff', border: 'none', borderRadius: 999,
                      fontSize: 12, fontWeight: 700, cursor: 'pointer'
                    }}
                  >
                    View All Profiles
                  </button>
                </div>
              )}

              {/* View All Button */}
              <button className="bm-view-all-btn" onClick={handleViewAll}>
                <FaArrowRight size={16} />
                View All
              </button>
            </div>
          </div>

        </div>
      </div>
    </>
  );
};

export default BiodataMatches;